import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { NAV, COMPANY } from "@/data/content";
import { useSite } from "@/context/SiteContext";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openQuote, waLink, config } = useSite();
  // Phase 11: logo/brand come from the live /api/config response (which
  // reflects this deployment's SITE_ID), falling back to the existing
  // Hindustan Solar Power defaults from data/content.js until that request
  // resolves — so nothing changes visually for the current deployment.
  const brandName = config?.brand || COMPANY.name;
  const logoSrc = config?.logo || "/logo.jpeg";
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#060B11]/95 shadow-[0_4px_30px_rgba(0,0,0,0.5)]" : "bg-[#060B11]/70"} backdrop-blur-xl border-b border-white/10`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <div className="bg-white rounded-lg p-1 flex items-center">
              <img src={logoSrc} alt={`${brandName} logo`} className="h-9 lg:h-11 w-auto" />
            </div>
            <div className="hidden xl:block text-[10px] text-emerald-400 font-mono tracking-widest uppercase leading-tight">Solar + Storage<br/>+ Power</div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${loc.pathname === n.to ? "text-emerald-400" : "text-slate-300 hover:text-white"}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="header-whatsapp-button" className="btn-ghost !py-2.5 !px-4 text-sm">
              <MessageCircle size={16} /> WhatsApp Us
            </a>
            <button onClick={() => openQuote("Header")} data-testid="header-get-quote-button" className="btn-primary !py-2.5 !px-5 text-sm">
              Get a Quote
            </button>
          </div>

          <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#060B11] border-t border-white/10 px-4 py-4" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="block px-3 py-3 text-slate-200 hover:text-emerald-400 border-b border-white/5 font-medium">
              {n.label}
            </Link>
          ))}
          <button onClick={() => openQuote("Mobile Header")} className="btn-primary w-full mt-4" data-testid="mobile-get-quote-button">Get a Quote</button>
        </div>
      )}
    </header>
  );
}
